export function resolveMetadataUserTypeName(metaEntry) {
  return String(
    metaEntry?.UserTypes?.user_type_name ??
      metaEntry?.user_types?.user_type_name ??
      metaEntry?.user_type_name ??
      ""
  )
    .trim()
    .toLowerCase();
}

export function isEmployeeMetadataEntry(metaEntry) {
  return resolveMetadataUserTypeName(metaEntry) === "employee";
}

export function isInternMetadataEntry(metaEntry) {
  return resolveMetadataUserTypeName(metaEntry) === "intern";
}

/** Organizations where the logged-in user is registered as Employee (attendance self-service). */
export function getEmployeeAttendanceOrganizations(user) {
  const allOrganizations = getClientOrganizations(user);

  const employeeOrgIds = new Set(
    (user?.metadata || [])
      .filter(isEmployeeMetadataEntry)
      .map((item) => Number(item?.organization_id))
      .filter(Boolean)
  );

  if (!employeeOrgIds.size) return [];

  return allOrganizations.filter((org) =>
    employeeOrgIds.has(Number(org?.organization_id))
  );
}

/** Organizations where the logged-in user is registered as Intern (attendance self-service). */
export function getInternAttendanceOrganizations(user) {
  const allOrganizations = getClientOrganizations(user);

  const internOrgIds = new Set(
    (user?.metadata || [])
      .filter(isInternMetadataEntry)
      .map((item) => Number(item?.organization_id))
      .filter(Boolean)
  );

  if (!internOrgIds.size) return [];

  return allOrganizations.filter((org) =>
    internOrgIds.has(Number(org?.organization_id))
  );
}

export function getSelfAttendanceOrganizations(user) {
  return getAttendanceOrganizationsForUser(user);
}

/** All organizations belonging to the logged-in client (from login / token-login). */
export function getClientOrganizations(user) {
  if (Array.isArray(user?.organizations) && user.organizations.length) {
    return user.organizations;
  }

  if (Array.isArray(user?.organization)) {
    return user.organization;
  }

  return user?.organization ? [user.organization] : [];
}

/** Keep the full client org list on the session (never collapse to active org only). */
export function ensureOrganizationsList(user) {
  if (!user) return user;

  const orgList = enrichOrganizationsWithSessionSettings(
    user,
    getClientOrganizations(user)
  );
  if (!orgList.length) return user;

  orgList.forEach((org) => {
    const kioskSound =
      org?.kiosk_sound_setting_value ?? org?.kiosk_sound_value ?? null;
    if (kioskSound != null && String(kioskSound).trim() !== "") {
      cacheKioskSoundValue(org.organization_id, kioskSound);
    }
  });

  return {
    ...user,
    organizations: orgList,
  };
}

export function resolveActiveAttendanceMarkingMode(user) {
  return (
    user?.user_active_organization?.settings?.setting_value ??
    user?.activeOrganization?.setting_value ??
    null
  );
}

export function isKioskSoundSettingName(settingName) {
  const normalized = normalizeSettingValue(settingName).replace(/_/g, " ");
  return normalized.includes("kiosk") && normalized.includes("sound");
}

export function resolveActiveKioskSoundSetting(user) {
  const activeSettings = user?.user_active_organization?.settings;
  if (
    activeSettings &&
    isKioskSoundSettingName(activeSettings?.setting_name)
  ) {
    return activeSettings?.setting_value ?? null;
  }

  const activeOrganization = user?.activeOrganization;
  if (
    activeOrganization &&
    isKioskSoundSettingName(activeOrganization?.setting_name)
  ) {
    return activeOrganization?.setting_value ?? null;
  }

  return null;
}

export function isKioskSoundEnabledForValue(value) {
  return normalizeSettingValue(value) === "on";
}

export function getKioskSoundSettingValueForOrganization(org) {
  if (!org) return null;

  const directValues = [
    org?.kiosk_sound_setting_value,
    org?.kiosk_sound_value,
  ];

  for (const value of directValues) {
    if (value != null && String(value).trim() !== "") {
      return value;
    }
  }

  const settingsList = Array.isArray(org?.settings)
    ? org.settings
    : Array.isArray(org?.organization_settings)
      ? org.organization_settings
      : [];

  const kioskSetting = settingsList.find((entry) =>
    isKioskSoundSettingName(
      entry?.setting_key ??
        entry?.key ??
        entry?.name ??
        entry?.setting_name ??
        ""
    )
  );

  if (
    kioskSetting &&
    kioskSetting.setting_value != null &&
    String(kioskSetting.setting_value).trim() !== ""
  ) {
    return kioskSetting.setting_value;
  }

  return null;
}

export function isKioskSoundEnabledForOrganization(org) {
  const value = getKioskSoundSettingValueForOrganization(org);
  if (value == null) return false;
  return isKioskSoundEnabledForValue(value);
}

/** Fill missing per-org settings from session/login data and active org. */
export function enrichOrganizationsWithSessionSettings(user, organizations = []) {
  const sessionOrgs = getClientOrganizations(user);
  const settingsByOrgId = new Map(
    sessionOrgs.map((org) => [
      Number(org.organization_id),
      org.setting_value ?? org.attendance_setting_value ?? null,
    ])
  );
  const kioskSoundByOrgId = new Map(
    sessionOrgs.map((org) => [
      Number(org.organization_id),
      org.kiosk_sound_setting_value ?? org.kiosk_sound_value ?? null,
    ])
  );

  const activeOrgId = Number(resolveActiveOrganizationId(user));
  const activeMode = resolveActiveAttendanceMarkingMode(user);
  const activeKioskSound = resolveActiveKioskSoundSetting(user);

  return (organizations || []).map((org) => {
    const orgId = Number(org?.organization_id);
    let settingValue =
      org.setting_value ??
      org.attendance_setting_value ??
      settingsByOrgId.get(orgId) ??
      null;

    if (!settingValue && activeOrgId && orgId === activeOrgId && activeMode) {
      settingValue = activeMode;
    }

    let kioskSoundValue =
      org.kiosk_sound_setting_value ??
      org.kiosk_sound_value ??
      kioskSoundByOrgId.get(orgId) ??
      null;

    if (
      !kioskSoundValue &&
      activeOrgId &&
      orgId === activeOrgId &&
      activeKioskSound
    ) {
      kioskSoundValue = activeKioskSound;
    }

    return {
      ...org,
      setting_value: settingValue,
      attendance_setting_value: settingValue,
      kiosk_sound_setting_value: kioskSoundValue,
      kiosk_sound_value: kioskSoundValue,
    };
  });
}

/** @deprecated Use enrichOrganizationsWithSessionSettings */
export function enrichOrganizationsWithAttendanceSettings(user, organizations = []) {
  return enrichOrganizationsWithSessionSettings(user, organizations);
}

export function resolveKioskSoundEnabled(user, organizationId = null) {
  const orgId =
    organizationId != null
      ? Number(organizationId)
      : Number(resolveActiveOrganizationId(user));

  if (!orgId) return false;

  const org = enrichOrganizationsWithSessionSettings(
    user,
    getClientOrganizations(user)
  ).find((entry) => Number(entry?.organization_id) === orgId);

  const sessionValue = getKioskSoundSettingValueForOrganization(org);
  if (sessionValue != null) {
    return isKioskSoundEnabledForValue(sessionValue);
  }

  const cached = getCachedKioskSoundValue(orgId);
  if (cached !== null) {
    return isKioskSoundEnabledForValue(cached);
  }

  return false;
}

const KIOSK_SOUND_CACHE_KEY_PREFIX = "attendance_kiosk_sound_";

export function getCachedKioskSoundValue(organizationId) {
  try {
    const raw = sessionStorage.getItem(
      `${KIOSK_SOUND_CACHE_KEY_PREFIX}${Number(organizationId)}`
    );
    return raw === null ? null : raw;
  } catch {
    return null;
  }
}

export function cacheKioskSoundValue(organizationId, value) {
  try {
    sessionStorage.setItem(
      `${KIOSK_SOUND_CACHE_KEY_PREFIX}${Number(organizationId)}`,
      String(value ?? "off")
    );
  } catch {
    // Ignore storage failures.
  }
}

export async function fetchKioskSoundSetting(organizationId) {
  const orgId = Number(organizationId);
  if (!orgId) return null;

  const cached = getCachedKioskSoundValue(orgId);
  if (cached !== null) return cached;

  const { MAIN_URL, getApiHeaders } = await import("../Configurations/Urls");
  const response = await fetch(`${MAIN_URL}/api/organizations/${orgId}/setting`, {
    headers: getApiHeaders(),
  });

  if (!response.ok) {
    return null;
  }

  const data = await response.json().catch(() => ({}));
  const settings = data?.settings;
  const list = Array.isArray(settings)
    ? settings
    : Array.isArray(settings?.data)
      ? settings.data
      : [];

  const kioskSetting = list.find((entry) =>
    isKioskSoundSettingName(entry?.setting_name)
  );
  const value = kioskSetting?.setting_value ?? null;
  cacheKioskSoundValue(orgId, value ?? "off");
  return value;
}

export async function resolveKioskSoundEnabledAsync(user, organizationId = null) {
  const orgId =
    organizationId != null
      ? Number(organizationId)
      : Number(resolveActiveOrganizationId(user));

  if (!orgId) return false;

  const org = enrichOrganizationsWithSessionSettings(
    user,
    getClientOrganizations(user)
  ).find((entry) => Number(entry?.organization_id) === orgId);

  const sessionValue = getKioskSoundSettingValueForOrganization(org);
  if (sessionValue != null) {
    return isKioskSoundEnabledForValue(sessionValue);
  }

  const fetched = await fetchKioskSoundSetting(orgId);
  if (fetched == null || String(fetched).trim() === "") {
    return false;
  }

  return isKioskSoundEnabledForValue(fetched);
}

function normalizeSettingValue(value) {
  return String(value ?? "")
    .trim()
    .toLowerCase();
}

export function isManagerAttendanceOrganization(org) {
  if (!org) return false;

  const direct = normalizeSettingValue(org?.setting_value);
  if (direct) return direct === "manager";

  const attendanceValue = normalizeSettingValue(org?.attendance_setting_value);
  if (attendanceValue) return attendanceValue === "manager";

  const nested = normalizeSettingValue(org?.settings?.setting_value);
  if (nested) return nested === "manager";

  const nestedAlt = normalizeSettingValue(
    org?.organization_settings?.setting_value
  );
  if (nestedAlt) return nestedAlt === "manager";

  const settingsList = Array.isArray(org?.settings)
    ? org.settings
    : Array.isArray(org?.organization_settings)
      ? org.organization_settings
      : [];

  if (settingsList.length) {
    const attendanceSetting = settingsList.find((entry) => {
      const key = String(
        entry?.setting_key ??
          entry?.key ??
          entry?.name ??
          entry?.setting_name ??
          ""
      )
        .trim()
        .toLowerCase();
      return (
        key === "attendance_marking_mode" ||
        key === "attendance marking mode" ||
        key === "attendance_mode"
      );
    });

    if (attendanceSetting) {
      return normalizeSettingValue(attendanceSetting?.setting_value) === "manager";
    }
  }

  return false;
}

export function getAllowedOrganizations(user) {
  const allOrganizations = getClientOrganizations(user);

  const allowedOrgIds = new Set(
    (user?.metadata || [])
      .map((item) => Number(item?.organization_id))
      .filter(Boolean)
  );

  if (!allowedOrgIds.size) return allOrganizations;

  return allOrganizations.filter((org) =>
    allowedOrgIds.has(Number(org?.organization_id))
  );
}

export function resolveActiveOrganizationId(user) {
  return (
    user?.user_active_organization?.organization_id ??
    user?.user_active_organization?.user_current_active_organization_id ??
    user?.activeOrganization?.organization_id ??
    user?.activeOrganization?.user_current_active_organization_id ??
    user?.organization?.organization_id ??
    null
  );
}

export function resolveOrganizationUserId(user) {
  const activeOrgId = resolveActiveOrganizationId(user);

  if (activeOrgId != null) {
    const matched = (user?.metadata || []).find(
      (item) => Number(item?.organization_id) === Number(activeOrgId)
    );
    if (matched?.organization_user_id) {
      return matched.organization_user_id;
    }
  }

  return (
    user?.organizationuser?.organization_user_id ??
    user?.metadata?.[0]?.organization_user_id ??
    null
  );
}

export function normalizeUserSession(user, organizationList = null) {
  const orgList = organizationList ?? getClientOrganizations(user);
  const fullOrgList = Array.isArray(orgList) ? orgList : orgList ? [orgList] : [];

  const activeOrgId = resolveActiveOrganizationId(user);
  const activeOrg =
    fullOrgList.find(
      (item) => Number(item?.organization_id) === Number(activeOrgId)
    ) ||
    fullOrgList[0] ||
    null;

  const mergedOrgUser =
    user?.organizationuser ||
    (Array.isArray(user?.metadata)
      ? user.metadata.find(
          (item) => Number(item?.organization_id) === Number(activeOrgId)
        )
      : null);

  return {
    ...user,
    organizations: fullOrgList,
    organization: activeOrg,
    organizationuser: mergedOrgUser ?? user?.organizationuser ?? null,
    user_active_organization:
      user?.user_active_organization ?? user?.activeOrganization ?? null,
  };
}

export function isInternLoginUser(user) {
  return user?.loginuser?.[0]?.intern_code !== undefined;
}

/** Active attendance person type for this user (employee | intern) from org metadata. */
export function resolveUserAttendancePersonType(user, organizationId = null) {
  const orgId =
    organizationId != null
      ? Number(organizationId)
      : Number(resolveActiveOrganizationId(user));

  if (orgId) {
    const metaEntry = (user?.metadata || []).find(
      (item) => Number(item?.organization_id) === orgId
    );
    const metaType = resolveMetadataUserTypeName(metaEntry);
    if (metaType === "intern" || metaType === "employee") {
      return metaType;
    }

    const orgUserType = String(
      user?.organizationuser?.user_types?.user_type_name ??
        user?.organizationuser?.UserTypes?.user_type_name ??
        ""
    )
      .trim()
      .toLowerCase();

    if (
      Number(user?.organizationuser?.organization_id) === orgId &&
      (orgUserType === "intern" || orgUserType === "employee")
    ) {
      return orgUserType;
    }
  }

  const hasInternMeta = (user?.metadata || []).some(isInternMetadataEntry);
  const hasEmployeeMeta = (user?.metadata || []).some(isEmployeeMetadataEntry);

  if (hasInternMeta && !hasEmployeeMeta) return "intern";
  if (hasEmployeeMeta && !hasInternMeta) return "employee";

  if (isInternLoginUser(user)) return "intern";
  return "employee";
}

export function isEmployeeAttendanceUser(user, organizationId = null) {
  return resolveUserAttendancePersonType(user, organizationId) === "employee";
}

export function isInternAttendanceUser(user, organizationId = null) {
  return resolveUserAttendancePersonType(user, organizationId) === "intern";
}

/** Organizations this user may use for attendance (scoped by user type). */
export function getAttendanceOrganizationsForUser(user) {
  return isInternAttendanceUser(user)
    ? getInternAttendanceOrganizations(user)
    : getEmployeeAttendanceOrganizations(user);
}

/** True for kiosk operators without a linked person record (can mark both types). */
export function isDedicatedKioskOperator(user) {
  return isManagerAttendanceMode(user) && !hasLinkedPersonRecord(user);
}

export function shouldShowKioskPersonTypeTabs(user) {
  return false;
}

export function resolveKioskPersonTypeForUser(user, state = null) {
  if (shouldShowKioskPersonTypeTabs(user) && state?.person_type === "intern") {
    return "intern";
  }
  if (shouldShowKioskPersonTypeTabs(user) && state?.person_type === "employee") {
    return "employee";
  }
  return isInternAttendanceUser(user) ? "intern" : "employee";
}

/** Reads Attendance Marking Mode from the active organization (never shown in UI). */
export function getAttendanceMarkingMode(user) {
  return (
    user?.user_active_organization?.settings?.setting_value ??
    user?.activeOrganization?.setting_value ??
    "self"
  );
}

export function isManagerAttendanceMode(user) {
  return getAttendanceMarkingMode(user) === "manager";
}

/** True when any client organization uses manager attendance marking mode. */
export function hasManagerAttendanceOrganization(user) {
  return getManagerAttendanceOrganizations(user).length > 0;
}

/** Attendance org picker: show manager-mode orgs regardless of active organization. */
export function shouldShowManagerAttendanceOrganizations(user) {
  if (!user) return false;
  if (isAdminKioskOperator(user)) return true;
  if (isAdminAttendanceUser(user) && hasManagerAttendanceOrganization(user)) {
    return true;
  }
  return hasManagerAttendanceOrganization(user);
}

export function isSelfAttendanceMode(user) {
  return getAttendanceMarkingMode(user) === "self";
}

export function resolveSystemRoleName(user) {
  const role = user?.role;
  if (typeof role === "string") {
    return role.trim();
  }
  return String(role?.system_role_name ?? user?.role_name ?? "").trim();
}

export function isAdminAttendanceUser(user) {
  const roleName = resolveSystemRoleName(user).toLowerCase();
  if (roleName === "admin") {
    return true;
  }

  const orgUserType = String(
    user?.organizationuser?.user_types?.user_type_name ??
      user?.organizationuser?.UserTypes?.user_type_name ??
      ""
  )
    .trim()
    .toLowerCase();

  if (orgUserType === "admin" || orgUserType.includes("admin")) {
    return true;
  }

  const hasEmployeeMeta = (user?.metadata || []).some(isEmployeeMetadataEntry);
  const hasInternMeta = (user?.metadata || []).some(isInternMetadataEntry);

  return !hasEmployeeMeta && !hasInternMeta && !hasLinkedPersonRecord(user);
}

/** Manager mode attendance login is restricted to admin accounts (employee+admin is allowed). */
export function canAccessManagerAttendanceLogin(user) {
  if (!isManagerAttendanceMode(user)) {
    return true;
  }

  if (isAdminAttendanceUser(user)) {
    return true;
  }

  if (isEmployeeAttendanceUser(user) || isInternAttendanceUser(user)) {
    return false;
  }

  return false;
}

export function getManagerAttendanceLoginDenialMessage(user) {
  if (isAdminAttendanceUser(user)) {
    return null;
  }

  if (isInternAttendanceUser(user)) {
    return "Manager attendance mode is enabled. Intern login is not allowed. Please use an admin account.";
  }

  if (isEmployeeAttendanceUser(user)) {
    return "Manager attendance mode is enabled. Employee login is not allowed. Please use an admin account.";
  }

  return "Manager attendance mode is enabled. Only admin users can access attendance login.";
}

export function assertManagerAttendanceLoginAllowed(user) {
  if (!isManagerAttendanceMode(user)) {
    return null;
  }

  if (canAccessManagerAttendanceLogin(user)) {
    return null;
  }

  return getManagerAttendanceLoginDenialMessage(user);
}

export function hasLinkedEmployeeRecord(user) {
  const loginUser = user?.loginuser?.[0];
  return Boolean(
    loginUser &&
      !isInternLoginUser(user) &&
      (loginUser.organization_person_id ||
        loginUser.employee_id ||
        loginUser.id)
  );
}

export function hasLinkedInternRecord(user) {
  const loginUser = user?.loginuser?.[0];
  return Boolean(
    loginUser &&
      isInternLoginUser(user) &&
      (loginUser.organization_person_id ||
        loginUser.intern_id ||
        loginUser.id)
  );
}

export function hasLinkedPersonRecord(user) {
  return isInternAttendanceUser(user)
    ? hasLinkedInternRecord(user)
    : hasLinkedEmployeeRecord(user);
}

/** Admin account without a linked employee/intern row (e.g. HR admin kiosk operator). */
export function isAdminKioskOperator(user) {
  return isAdminAttendanceUser(user) && !hasLinkedPersonRecord(user);
}

export function shouldUseManagerKioskFlow(user) {
  if (isAdminKioskOperator(user)) return true;
  if (isAdminAttendanceUser(user) && hasManagerAttendanceOrganization(user)) {
    return true;
  }
  if (isManagerAttendanceMode(user)) return true;
  return false;
}

export function canAccessAttendanceLogin(user) {
  if (isManagerAttendanceMode(user)) {
    return canAccessManagerAttendanceLogin(user);
  }
  if (hasLinkedPersonRecord(user)) return true;
  if (isAdminAttendanceUser(user)) return true;
  return false;
}

export function buildManagerKioskState(org, personType = "employee") {
  if (!org?.organization_id) return null;

  return {
    organization_id: org.organization_id,
    organization_name: org.organization_name || "",
    organization_short_name: org.organization_short_name || "",
    client_id: org.client_id,
    kiosk_mode: true,
    self_attendance: false,
    person_type: personType === "intern" ? "intern" : "employee",
  };
}

export function buildManagerKioskStateForUser(user, org) {
  const personType = resolveKioskPersonTypeForUser(user);
  return buildManagerKioskState(org, personType);
}

export function isManagerKioskPageState(state, user) {
  if (state?.kiosk_mode === true || state?.self_attendance === false) {
    return Boolean(
      state?.organization_id &&
        !state?.organization_person_id &&
        !state?.employee_id &&
        !state?.intern_id
    );
  }

  return (
    isManagerAttendanceMode(user) &&
    Boolean(state?.organization_id) &&
    !state?.organization_person_id &&
    !state?.employee_id &&
    !state?.intern_id
  );
}

export function resolveKioskPersonType(state, user = null) {
  if (user) {
    return resolveKioskPersonTypeForUser(user, state);
  }
  return state?.person_type === "intern" ? "intern" : "employee";
}

export function buildFaceAuthNavigationItem(user) {
  const loginUser = user?.loginuser?.[0];
  if (!loginUser) return null;

  const isIntern = isInternAttendanceUser(user);
  const personKey = isIntern ? "intern_id" : "employee_id";

  return {
    organization_id:
      user?.user_active_organization?.organization?.organization_id ??
      user?.organization?.organization_id ??
      resolveActiveOrganizationId(user),
    organization_name:
      user?.user_active_organization?.organization?.organization_name ??
      user?.organization?.organization_name ??
      "",
    [personKey]: loginUser[personKey],
    organization_person_id: loginUser.organization_person_id,
    employee_details: loginUser,
    self_attendance: true,
    person_type: isIntern ? "intern" : "employee",
  };
}

const ATTENDANCE_LANDING_STATE_KEY = "attendance_landing_state";

export function buildAttendanceLandingState(user) {
  if (shouldUseManagerKioskFlow(user)) {
    const org = user?.organization;
    return org ? buildManagerKioskStateForUser(user, org) : null;
  }

  const personItem = buildFaceAuthNavigationItem(user);
  if (!personItem) return null;

  return personItem;
}

export function persistAttendanceLandingState(state) {
  if (!state?.organization_id) return;

  const isKiosk = state.kiosk_mode === true || state.self_attendance === false;
  if (!isKiosk && !state?.organization_person_id) return;

  try {
    sessionStorage.setItem(
      ATTENDANCE_LANDING_STATE_KEY,
      JSON.stringify(state)
    );
  } catch {
    // Ignore storage failures (private mode / blocked storage).
  }
}

export function readAttendanceLandingState() {
  try {
    const raw = sessionStorage.getItem(ATTENDANCE_LANDING_STATE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearAttendanceLandingState() {
  try {
    sessionStorage.removeItem(ATTENDANCE_LANDING_STATE_KEY);
  } catch {
    // Ignore storage failures.
  }
}

export function resolveAttendancePageState(user, locationState = null) {
  const location = locationState || {};

  if (isManagerKioskPageState(location, user)) {
    const activeOrg = user?.organization;
    return {
      ...location,
      organization_id:
        location.organization_id ?? resolveActiveOrganizationId(user),
      organization_name:
        location.organization_name ??
        activeOrg?.organization_name ??
        "",
      kiosk_mode: true,
      self_attendance: false,
    };
  }

  const persisted = readAttendanceLandingState();
  const fromUser = buildAttendanceLandingState(user);

  if (persisted?.kiosk_mode || persisted?.self_attendance === false) {
    return {
      ...persisted,
      ...location,
    };
  }

  return {
    ...fromUser,
    ...persisted,
    ...location,
  };
}

/** Organizations available on the manager-mode kiosk (admin accounts). */
export function mergeOrganizationAttendanceSettings(user, organizations = []) {
  return enrichOrganizationsWithSessionSettings(user, organizations);
}

export function getManagerAttendanceOrganizations(user) {
  const managerOrgs = enrichOrganizationsWithSessionSettings(
    user,
    getClientOrganizations(user)
  ).filter(isManagerAttendanceOrganization);

  if (managerOrgs.length) {
    return managerOrgs;
  }

  if (!isManagerAttendanceMode(user)) {
    return [];
  }

  const activeOrgId = Number(resolveActiveOrganizationId(user));
  const activeMode = resolveActiveAttendanceMarkingMode(user);
  if (
    !activeOrgId ||
    normalizeSettingValue(activeMode) !== "manager"
  ) {
    return [];
  }

  const activeOrg =
    getClientOrganizations(user).find(
      (org) => Number(org?.organization_id) === activeOrgId
    ) ||
    user?.user_active_organization?.organization ||
    user?.activeOrganizationEntity ||
    null;

  if (!activeOrg) {
    return [];
  }

  return enrichOrganizationsWithSessionSettings(user, [activeOrg]).filter(
    isManagerAttendanceOrganization
  );
}

/** Organizations shown on attendance login picker for the current user. */
export function getAttendanceLoginOrganizations(user) {
  if (shouldShowManagerAttendanceOrganizations(user)) {
    return getManagerAttendanceOrganizations(user);
  }

  if (
    isAdminAttendanceUser(user) ||
    shouldUseManagerKioskFlow(user) ||
    isManagerAttendanceMode(user)
  ) {
    return getClientOrganizations(user);
  }

  return getAttendanceOrganizationsForUser(user);
}

export function resolveDefaultAttendanceOrganization(user, organizations = null) {
  const orgs = organizations ?? getAttendanceLoginOrganizations(user);

  if (!orgs.length) return null;

  return orgs[0];
}

export function resolveDefaultAttendanceOrganizationId(
  user,
  organizations = null
) {
  return (
    resolveDefaultAttendanceOrganization(user, organizations)
      ?.organization_id ?? null
  );
}

/** Manager kiosk: direct face scan when one manager org; org picker when multiple. */
export function navigateAfterManagerLogin(user, navigate) {
  if (!user) return false;

  const orgs = getManagerAttendanceOrganizations(user);
  if (!orgs.length) return false;

  if (orgs.length === 1) {
    const state = buildManagerKioskStateForUser(user, orgs[0]);
    if (!state) return false;
    persistAttendanceLandingState(state);
    navigate("/", { state, replace: true });
    return true;
  }

  clearAttendanceLandingState();
  navigate("/organization-toggle", { replace: true });
  return true;
}

/** Self attendance: org picker when multiple; direct page when one. */
export function navigateAfterSelfAttendanceLogin(user, navigate, setMode) {
  if (!user) return false;

  const orgs = getAttendanceOrganizationsForUser(user);
  if (!orgs.length) return false;

  if (orgs.length === 1) {
    return navigateAfterAttendanceLogin(user, navigate, setMode);
  }

  clearAttendanceLandingState();
  navigate("/organization-toggle", { replace: true });
  return true;
}

/** After login open attendance page with the logged-in employee pre-selected (self mode). */
export function navigateAfterAttendanceLogin(user, navigate, setMode) {
  if (!user) return false;

  if (shouldUseManagerKioskFlow(user)) {
    return navigateAfterManagerLogin(user, navigate);
  }

  if (!hasLinkedPersonRecord(user)) {
    return false;
  }

  setMode(isInternAttendanceUser(user) ? 2 : 1);

  const state = buildAttendanceLandingState(user);
  if (!state) return false;

  persistAttendanceLandingState(state);
  navigate("/", { state, replace: true });
  return true;
}
